import { useCallback, useEffect, useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import {
  useInfiniteListDirectoryByFamily,
  useInfiniteListDirectoryByIndividual,
} from '@givebacks/api/memberhub';
import { AxiosError } from 'axios';

import { DirectoryListItems } from './components/DirectoryListItems';

const DirectoryList = ({
  groupBy,
  search,
  organizationId,
  setHasAccess,
}: {
  groupBy: 'families' | 'individuals';
  search: string;
  organizationId?: string;
  setHasAccess: (hasAccess: boolean) => void;
}) => {
  const { isAuthenticated } = useAuthentication();

  const {
    data: familiesData,
    hasNextPage: familiesHasNextPage,
    fetchNextPage: familiesFetchNextPage,
    isFetchingNextPage: familiesIsFetchingNextPage,
    error: familiesError,
  } = useInfiniteListDirectoryByFamily({
    organizationId,
    queryString: search,
    enabled: isAuthenticated && groupBy === 'families',
  });

  const {
    data: individualsData,
    hasNextPage: individualsHasNextPage,
    fetchNextPage: individualsFetchNextPage,
    isFetchingNextPage: individualsIsFetchingNextPage,
    error: individualsError,
  } = useInfiniteListDirectoryByIndividual({
    organizationId,
    queryString: search,
    enabled: isAuthenticated && groupBy === 'individuals',
  });

  const families = useMemo(
    () => familiesData?.pages.flatMap((page) => page.families),
    [familiesData]
  );

  const individuals = useMemo(
    () => individualsData?.pages.flatMap((page) => page.users),
    [individualsData]
  );

  const items = useMemo(() => {
    if (groupBy === 'families') return families;
    if (groupBy === 'individuals') return individuals;
    return [];
  }, [groupBy, families, individuals]);

  const error = useMemo(() => {
    if (familiesError) return familiesError;
    if (individualsError) return individualsError;
    return null;
  }, [familiesError, individualsError]);

  const hasNextPage = useMemo(() => {
    if (groupBy === 'families') return familiesHasNextPage;
    if (groupBy === 'individuals') return individualsHasNextPage;
    return false;
  }, [groupBy, familiesHasNextPage, individualsHasNextPage]);

  const fetchNextPage = useCallback(() => {
    if (groupBy === 'families') {
      void familiesFetchNextPage();
    } else {
      void individualsFetchNextPage();
    }
  }, [groupBy, familiesFetchNextPage, individualsFetchNextPage]);

  const isFetchingNextPage = useMemo(() => {
    if (groupBy === 'families') return familiesIsFetchingNextPage;
    if (groupBy === 'individuals') return individualsIsFetchingNextPage;
    return false;
  }, [groupBy, familiesIsFetchingNextPage, individualsIsFetchingNextPage]);

  useEffect(() => {
    if ((error as AxiosError)?.response?.status === 401) {
      setHasAccess(false);
    }
  }, [error, setHasAccess]);

  if (!organizationId || !items || items.length === 0) return null;

  return (
    <DirectoryListItems
      items={items}
      itemsType={groupBy === 'families' ? 'family' : 'user'}
      hasNextPage={hasNextPage}
      fetchNextPage={() => fetchNextPage()}
      isFetchingNextPage={isFetchingNextPage}
    />
  );
};

export { DirectoryList };
